import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { Link as RouterLink } from "react-router-dom";

import MODALS from "../../../constants/modals";
import { useAppDispatch } from "../../../hooks/redux";
import { openModal } from "../../../features/modals";
import axios from "axios";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";

import {
  Stack,
  Grid,
  Typography,
  IconButton,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";

import {
  REPORT_STATUSES,
  ReportStatuses,
} from "../../../constants/reportStatuses";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import {
  IDailySheetReportRecordResponse,
  IDailySheetReportResponse,
} from "../../../types/dailySheetReport";

const ViewDailySheetReport: React.FunctionComponent = () => {
  const { clinic, id } = useParams<{ clinic: string; id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [report, setReport] = useState<IDailySheetReportResponse>();
  const [records, setRecords] = useState<IDailySheetReportRecordResponse[]>([]);

  const [lastActiveId, setLastActiveId] = useState<string | null>(null);

  const handleReportClose = useCallback(() => {
    if (report) {
      dispatch(
        openModal({
          type: MODALS.Approve,
          title: "Aizvērt un nosūtīt atskaiti?",
          message:
            "Atskaiti vairs nebūs iespējams rediģēt un tā tiks nosūtīta uz --",
          onApprove: () => {
            const toastId = toast.loading("Notiek atskaites sūtīšana...");
            axios
              .post<IDailySheetReportResponse>(
                `/daily-sheets/close/${report._id}`
              )
              .then((res) => {
                toast.update(toastId, {
                  render: "Atskaite veiksmīgi nosūtīta",
                  type: "success",
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
                setReport(res.data);
              })
              .catch((err) => {
                toast.update(toastId, {
                  render:
                    "Notika neparedzēta kļūda, atskaite, iespējams, netika nosūtīta",
                  type: "error",
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
                console.log("error:", err);
              });
          },
        })
      );
    }
  }, [report]);

  const handleReportDelete = useCallback(() => {
    if (!report) {
      return;
    }
    dispatch(
      openModal({
        type: MODALS.Approve,
        title: "Vai tiešām vēlaties dzēst?",
        message: "Atskaite tiks neatgriezeniski dzēsta",
        onApprove: () => {
          axios
            .delete<IDailySheetReportRecordResponse>(
              `/daily-sheets/${report._id}`
            )
            .then((res) => {
              navigate(`/${clinic}/daily-sheets`);
            })
            .catch((err) => {
              console.log("error:", err);
            });
        },
      })
    );
  }, [report]);

  const loadData = useCallback(() => {
    setIsLoading(true);
    axios
      .get<{
        report: IDailySheetReportResponse;
        records: IDailySheetReportRecordResponse[];
      }>(`/daily-sheets/${clinic}/${id}`)
      .then((res) => {
        setReport(res.data.report);
        setRecords(res.data.records);
      })
      .catch((err) => {
        console.log("error:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [clinic, id]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (lastActiveId) {
      setTimeout(() => {
        setLastActiveId(null);
      }, 1700);
    }
  }, [lastActiveId]);

  const columns = useMemo<MRT_ColumnDef<IDailySheetReportRecordResponse>[]>(
    () => [
      {
        accessorKey: "patient",
        header: "Pacients",
        accessorFn: (params) => params.patient || "",
        sortable: false,
      },
      {
        accessorKey: "workplacePreparation",
        header: "Darba vietas sagatavošana (kods 2019)",
        type: "number",
        accessorFn: (params) => params.workplacePreparation?.toFixed(2) || "",
        sortable: false,
      },
      {
        accessorKey: "therapy",
        header: "Terapija",
        type: "number",
        accessorFn: (params) => params.therapy?.toFixed(2) || "",
        sortable: false,
      },
      {
        accessorKey: "surgery",
        header: "Ķirurģija",
        type: "number",
        accessorFn: (params) => params.surgery?.toFixed(2) || "",
        sortable: false,
      },
      {
        accessorKey: "prosthetics",
        header: "Protezēšana",
        type: "number",
        accessorFn: (params) => params.prosthetics?.toFixed(2) || "",
        sortable: false,
      },
      {
        accessorKey: "statePaid",
        header: "Bērni - valsts apmaksātie pakalpojumi",
        type: "number",
        accessorFn: (params) => params.statePaid?.toFixed(2) || "",
        sortable: false,
      },
      {
        accessorKey: "roentgen",
        header: "RTG",
        type: "number",
        accessorFn: (params) => params.roentgen?.toFixed(2) || "",
        sortable: false,
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <Grid container justifyContent="space-between">
        <Grid item container alignItems="center" spacing={1} xs>
          <Grid item>
            <IconButton component={RouterLink} to={`/${clinic}/daily-sheets`}>
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item md>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h4">Dienas lapa</Typography>
              {isLoading ? (
                <Skeleton width={200} height={40} />
              ) : (
                report && (
                  <>
                    <Typography variant="h4">
                      {moment(report.date).format("L")}
                    </Typography>
                    <Chip
                      label={REPORT_STATUSES[report.status].label}
                      variant={
                        report.status === ReportStatuses.Opened
                          ? "filled"
                          : "outlined"
                      }
                      size="small"
                      color={
                        report.status === ReportStatuses.Opened
                          ? "success"
                          : "default"
                      }
                    />
                  </>
                )
              )}
            </Stack>
          </Grid>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              startIcon={<AddIcon />}
              disabled={
                !Boolean(clinic) ||
                !id ||
                isLoading ||
                report?.status !== ReportStatuses.Opened
              }
              onClick={() => {
                if (!report || !clinic) {
                  return;
                }

                dispatch(
                  openModal({
                    type: MODALS.DailySheetReportRecord,
                    report: report._id,
                    clinic: clinic,
                    callback: loadData,
                  })
                );
              }}
            >
              Pievienot
            </LoadingButton>
            <IconButton aria-haspopup="true" onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleReportClose();
                }}
                disableRipple
              >
                <ListItemIcon>
                  <ForwardToInboxIcon />
                </ListItemIcon>
                <ListItemText>
                  {report?.status === ReportStatuses.Closed
                    ? "Nosūtīt atkārtoti"
                    : "Aizvērt un nosūtīt"}
                </ListItemText>
              </MenuItem>
              <MenuItem
                disabled={report?.status !== ReportStatuses.Opened}
                onClick={() => {
                  handleMenuClose();
                  handleReportDelete();
                }}
                disableRipple
              >
                <ListItemIcon>
                  <DeleteIcon />
                </ListItemIcon>
                <ListItemText>Dzēst atskaiti</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
        </Grid>
      </Grid>

      <MaterialReactTable
        columns={columns}
        enableColumnActions={false}
        data={records}
        getRowId={(row) => row._id}
        enableTableFooter={false}
        enableGlobalFilter={false}
        enableBottomToolbar={false}
        state={{
          isLoading,
          pagination: {
            pageIndex: 0,
            pageSize: records.length || 10,
          },
        }}
        muiTableBodyRowProps={(props) => ({
          onClick: () => {
            if (!report || !clinic) {
              return;
            }

            dispatch(
              openModal({
                type: MODALS.DailySheetReportRecord,
                report: report._id,
                clinic: clinic,
                record: props.row.original,
                callback: loadData,
              })
            );
          },
          style: { cursor: "pointer" },
        })}
      />
    </Stack>
  );
};

export default ViewDailySheetReport;
